import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { catalogApi, internshipApi, studentApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  PageHeader,
  ProgressBar,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { CvGoalCheck } from '@/features/career/CvGoalCheck';
import { Roadmap } from '@/features/career/Roadmap';
import { InternshipCard } from '@/features/internships/InternshipCard';
import { useSaveInternship } from '@/features/internships/useSaveInternship';
import { UpgradeNotice } from '@/features/studentBilling/UpgradeNotice';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { careerGoalSchema } from '@/validations';

/** How the field was arrived at, said plainly rather than hidden. */
const SOURCE_NOTE = {
  ai: 'Suggested by AI from what you wrote',
  keywords: 'Matched on the words you used',
};

/** Roles shown inline before sending the student to full discovery. */
const PREVIEW_ROLES = 5;

/**
 * Career guidance for a student with nothing to match on yet.
 *
 * A fresher has no experience and no tagged skills, so the matcher has nothing
 * to read and a ranked list means nothing to them. What they do have is an
 * intention. This page takes that sentence, proposes a field for it, shows what
 * open listings in that field are currently asking for, and lists the roles it
 * would put them in front of.
 *
 * Two rules hold throughout. Nothing reaches the profile until the student
 * confirms it, because a platform that quietly decided somebody's career would
 * be worse than one that never asked. And every skill on the path carries the
 * count it came from, so the advice is never "learn SQL" but "17 of 23 open
 * listings ask for SQL" -- which stops being true on its own when the market
 * moves, instead of sitting here as an opinion with no expiry date.
 */
export function StudentCareerPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const save = useSaveInternship();

  /** The last goal submitted, so confirming saves the words they wrote. */
  const [statedGoal, setStatedGoal] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  /** A field being looked at without committing to it. */
  const [preview, setPreview] = useState('');

  const profileQuery = useQuery({
    queryKey: ['student-profile'],
    queryFn: studentApi.getProfile,
  });

  const { data: categories } = useQuery({
    queryKey: ['job-categories'],
    queryFn: catalogApi.listJobCategories,
  });

  const profile = profileQuery.data;
  const savedCategory = profile?.target_job_category ?? null;
  // A preview wins while the student is browsing; their own field is the floor.
  const activeCategoryId = preview || savedCategory?.id || '';
  const isSavedField = Boolean(activeCategoryId) && activeCategoryId === savedCategory?.id;

  const pathQuery = useQuery({
    queryKey: ['career-path', activeCategoryId],
    queryFn: () => studentApi.getCareerPath(activeCategoryId),
    enabled: Boolean(activeCategoryId),
  });

  // The roadmap answers the goal just typed, or the one already saved.
  const roadmapGoal = statedGoal || profile?.career_goal || '';
  const roadmapKey = ['career-roadmap', activeCategoryId, roadmapGoal];
  const roadmapQuery = useQuery({
    queryKey: roadmapKey,
    queryFn: () =>
      studentApi.getCareerRoadmap({ goal: roadmapGoal, jobCategoryId: activeCategoryId }),
    enabled: Boolean(activeCategoryId),
    // Written once per goal and cached on the server; no reason to ask again
    // while the student is still reading it.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const newTake = useMutation({
    mutationFn: () =>
      studentApi.getCareerRoadmap({
        goal: roadmapGoal,
        jobCategoryId: activeCategoryId,
        refresh: true,
      }),
    onSuccess: (roadmap) => queryClient.setQueryData(roadmapKey, roadmap),
    onError: (error) => toast.error(errorMessage(error, 'Could not write a new roadmap')),
  });

  const rolesFilters = {
    job_category_id: activeCategoryId,
    page_size: PREVIEW_ROLES,
    sort: 'recent',
  };
  const rolesQuery = useQuery({
    queryKey: ['internships', rolesFilters],
    queryFn: () => internshipApi.searchInternships(rolesFilters),
    enabled: Boolean(activeCategoryId),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(careerGoalSchema), defaultValues: { goal: '' } });

  // Show back the goal already on the profile, once it arrives.
  useEffect(() => {
    if (profile?.career_goal) reset({ goal: profile.career_goal });
  }, [profile?.career_goal, reset]);

  async function onSubmit(values) {
    setStatedGoal(values.goal);
    try {
      const result = await studentApi.suggestCareerGoal(values.goal);
      setSuggestion(result);
      // The reading replaces whatever was being previewed, so the path below is
      // the answer to the question just asked.
      setPreview(result.category?.id ?? '');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not read that goal'));
    }
  }

  const confirm = useMutation({
    mutationFn: ({ categoryId, goal }) =>
      studentApi.updateProfile({
        target_job_category_id: categoryId,
        // Only overwrite the stored sentence when a new one has been typed.
        ...(goal ? { career_goal: goal } : {}),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['student-profile'], updated);
      setPreview('');
      setSuggestion(null);
      toast.success('Saved. Your recommendations now aim at this field.');
      [
        'student-dashboard',
        'recommendations',
        'career-path',
        'career-roadmap',
        'profile-completion',
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not save your field')),
  });

  const declareSkill = useMutation({
    // Their own declaration, at the level that overstates it least.
    mutationFn: (name) => studentApi.addSkill({ skill_name: name, proficiency: 'BEGINNER' }),
    onSuccess: () => {
      toast.success('Added to your skills. Set the level on the Skills page.');
      [
        'career-path',
        'career-roadmap',
        'student-skills',
        'student-profile',
        'recommendations',
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not add the skill')),
  });

  if (profileQuery.isPending) return <LoadingState label="Loading your profile…" />;
  if (profileQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(profileQuery.error)}
        onRetry={() => void profileQuery.refetch()}
      />
    );
  }

  const path = pathQuery.data;
  const steps = path?.steps ?? [];
  const held = steps.filter((step) => step.already_have).length;
  const roles = rolesQuery.data?.data ?? [];
  const openRoles = rolesQuery.data?.meta?.total_items ?? 0;
  const activeName =
    path?.category?.name ??
    categories?.find((category) => category.id === activeCategoryId)?.name ??
    savedCategory?.name;

  return (
    <div>
      <PageHeader
        title="Career path"
        description="No experience yet is a starting point, not a problem. Say where you want to end up and we will work backwards from the roles that are open today."
      />

      <div className="space-y-6">
        <Card>
          <CardHeader
            title="Where do you want to end up?"
            description="A sentence is enough — “I want to work with data”, “I want to design apps”."
          />
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <Textarea
              label="What you want to do"
              rows={3}
              placeholder="I want to build websites and eventually work as a frontend developer."
              error={errors.goal?.message}
              {...register('goal')}
            />
            <Button type="submit" isLoading={isSubmitting}>
              Read my goal
            </Button>
          </form>

          {suggestion?.category && (
            <FieldSuggestions
              suggestion={suggestion}
              activeId={activeCategoryId}
              savedId={savedCategory?.id}
              isSaving={confirm.isPending}
              onPreview={(id) => setPreview(id)}
              onChoose={(id) => confirm.mutate({ categoryId: id, goal: statedGoal })}
            />
          )}

          {suggestion && !suggestion.category && (
            <Alert tone="warning" className="mt-4" title="We could not place that in a field">
              That does not mean it is not a career — only that we could not match it to a field
              companies are hiring in here. Pick the closest one below.
            </Alert>
          )}

          <div className="border-line mt-6 grid gap-4 border-t pt-6 sm:grid-cols-[1fr_auto] sm:items-end">
            <Select
              label="Or choose a field yourself"
              hint={
                savedCategory
                  ? `Your field is ${savedCategory.name}. Choosing another only previews it.`
                  : 'Previewing a field changes nothing until you save it.'
              }
              value={activeCategoryId}
              onChange={(event) => {
                setPreview(event.target.value);
                setSuggestion(null);
              }}
            >
              <option value="">Select a field…</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
            {activeCategoryId && !isSavedField && (
              <Button
                variant="secondary"
                isLoading={confirm.isPending}
                onClick={() => confirm.mutate({ categoryId: activeCategoryId, goal: statedGoal })}
              >
                Make this my field
              </Button>
            )}
          </div>
        </Card>

        {!activeCategoryId && (
          <Card>
            <EmptyState
              title="Nothing to plan against yet"
              description="Tell us what you want to do, or pick a field, and we will show you what it is asking for."
            />
          </Card>
        )}

        {activeCategoryId && (
          <Card>
            <CardHeader
              title={
                roadmapGoal ? 'Your roadmap' : `Your roadmap into ${activeName ?? 'this field'}`
              }
              description={
                roadmapGoal
                  ? `From “${roadmapGoal}” to your first internship, step by step.`
                  : 'Tell us your goal above for a plan aimed at the exact role you want.'
              }
            />

            {roadmapQuery.isPending && (
              <LoadingState label="Writing your roadmap… the first time for a goal can take up to half a minute." />
            )}
            {roadmapQuery.isError && (
              <ErrorState
                message={errorMessage(roadmapQuery.error)}
                onRetry={() => void roadmapQuery.refetch()}
              />
            )}
            {roadmapQuery.data?.limit_reached && (
              <UpgradeNotice title="AI roadmaps used up for this month" className="mb-4">
                {roadmapQuery.data.upgrade_hint ??
                  "You've used your AI roadmaps this month — showing the skills open listings ask for. Upgrade for unlimited AI roadmaps."}
              </UpgradeNotice>
            )}
            {roadmapQuery.data && (
              <Roadmap
                roadmap={roadmapQuery.data}
                onKnowSkill={(name) => declareSkill.mutate(name)}
                knowingSkill={declareSkill.isPending ? declareSkill.variables : null}
                onRefresh={() => newTake.mutate()}
                isRefreshing={newTake.isPending}
              />
            )}
          </Card>
        )}

        {activeCategoryId && (
          <Card>
            <CardHeader
              title={
                activeName ? `What ${activeName} is asking for` : 'What this field is asking for'
              }
              description={
                path
                  ? `Counted from the ${path.open_listings} open listing(s) in this field right now — not a syllabus we wrote.`
                  : undefined
              }
              action={
                isSavedField ? <Badge tone="success">Your field</Badge> : <Badge>Preview</Badge>
              }
            />

            {pathQuery.isPending && <LoadingState label="Counting what this field asks for…" />}
            {pathQuery.isError && (
              <ErrorState
                message={errorMessage(pathQuery.error)}
                onRetry={() => void pathQuery.refetch()}
              />
            )}

            {path && steps.length === 0 && (
              <EmptyState
                title="Nothing open in this field is naming a skill yet"
                description="With no listings to count, there is no honest path to draw. Try another field, or come back once companies have published here."
              />
            )}

            {steps.length > 0 && (
              <>
                <ProgressBar
                  value={Math.round((held / steps.length) * 100)}
                  label={`${held} of ${steps.length} already on your profile`}
                />
                <ul className="divide-line mt-4 divide-y">
                  {steps.map((step) => (
                    <li
                      key={step.skill.id}
                      className="flex flex-wrap items-center justify-between gap-3 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-ink text-sm font-medium">{step.skill.name}</p>
                        <p className="text-ink-muted text-xs">
                          Asked for in {step.listings} of {path.open_listings} open listing(s)
                          {step.required_in > 0 && `, required in ${step.required_in}`}
                        </p>
                      </div>
                      {step.already_have ? (
                        <Badge tone="success">On your profile</Badge>
                      ) : (
                        <Button
                          variant="tintPositive"
                          size="sm"
                          className="shrink-0"
                          title={`I know ${step.skill.name}`}
                          isLoading={
                            declareSkill.isPending && declareSkill.variables === step.skill.name
                          }
                          onClick={() => declareSkill.mutate(step.skill.name)}
                        >
                          <Icon name="plus" size={14} />I know this
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-ink-muted mt-4 text-xs">
                  Ordered by how often listings ask for it, then by how often it is non-negotiable.
                  Start at the top.
                </p>
              </>
            )}
          </Card>
        )}

        {activeCategoryId && (
          // Linked from the resume gap analysis as #cv-check.
          <div id="cv-check" className="scroll-mt-20">
            <CvGoalCheck
              goal={roadmapGoal}
              categoryId={activeCategoryId}
              categoryName={activeName}
            />
          </div>
        )}

        {activeCategoryId && (
          <Card>
            <CardHeader
              title={activeName ? `Internships in ${activeName}` : 'Internships in this field'}
              description={
                isSavedField
                  ? 'Your field also feeds the ranking on For you.'
                  : 'What you would be applying to if this were your field.'
              }
              action={
                openRoles > roles.length ? (
                  <LinkButton
                    to={`${paths.internships}?job_category_id=${activeCategoryId}`}
                    variant="secondary"
                    size="sm"
                  >
                    All {openRoles}
                  </LinkButton>
                ) : undefined
              }
            />

            {rolesQuery.isPending && <LoadingState label="Finding open roles…" />}
            {rolesQuery.isError && (
              <ErrorState
                message={errorMessage(rolesQuery.error)}
                onRetry={() => void rolesQuery.refetch()}
              />
            )}

            {rolesQuery.data && roles.length === 0 && (
              <EmptyState
                title="Nothing open in this field right now"
                description="Save the field anyway — you will be ranked against new listings as companies publish them."
              />
            )}

            {roles.length > 0 && (
              <div className="grid gap-4">
                {roles.map((internship) => (
                  <InternshipCard
                    key={internship.id}
                    internship={internship}
                    onToggleSave={save.canSave ? save.toggle : undefined}
                    isSaving={save.isPending && save.pendingId === internship.id}
                  />
                ))}
              </div>
            )}
          </Card>
        )}

        {isSavedField && (
          <Alert tone="info" title="What this does, and what it does not">
            Your field is on your profile now, so it counts in matching — but an intention is not a
            qualification, and it is weighted as the weaker evidence it is. Every skill you add from
            the path above moves your ranking further than the goal alone ever will.{' '}
            <LinkButton to={paths.student.recommendations} variant="ghost" size="sm">
              See what fits you
            </LinkButton>
          </Alert>
        )}
      </div>
    </div>
  );
}

/**
 * The fields a goal was read as, best first -- each with why, and how many
 * internships are open in it now (counted, not generated). Choosing one only
 * previews it; "Make this my field" is what saves.
 */
function FieldSuggestions({ suggestion, activeId, savedId, isSaving, onPreview, onChoose }) {
  const fields = suggestion.suggestions?.length
    ? suggestion.suggestions
    : [{ ...suggestion.category, reason: null, open_listings: null }];

  return (
    <div className="border-brand-100 bg-brand-50/40 mt-4 rounded-xl border p-4">
      <p className="text-ink flex flex-wrap items-center gap-2 text-sm font-semibold">
        <Icon name="sparkles" size={16} className="text-brand-600" />
        {fields.length === 1 ? 'The field that fits best' : 'Fields that fit your goal'}
        <span className="text-ink-muted text-xs font-normal">
          {SOURCE_NOTE[suggestion.source] ?? 'Based on what you wrote'} · nothing is saved until you
          choose
        </span>
      </p>
      <ul className="mt-3 grid gap-3 sm:grid-cols-3">
        {fields.map((field, index) => {
          const active = field.id === activeId;
          const saved = field.id === savedId;
          return (
            <li key={field.id}>
              <div
                className={cn(
                  'bg-surface flex h-full flex-col rounded-lg border p-3 transition',
                  active ? 'border-brand-400 ring-brand-100 ring-2' : 'border-line',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-ink text-sm font-semibold">{field.name}</p>
                  {index === 0 && <Badge tone="brand">Best match</Badge>}
                </div>
                {field.reason && (
                  <p className="text-ink-muted mt-1 text-xs leading-5">{field.reason}</p>
                )}
                {field.open_listings != null && (
                  <p className="text-ink-muted mt-2 flex items-center gap-1 text-xs">
                    <Icon name="briefcase" size={13} />
                    {field.open_listings === 1
                      ? '1 open internship'
                      : `${field.open_listings} open internships`}
                  </p>
                )}
                <div className="mt-auto flex flex-wrap gap-2 pt-3">
                  {saved ? (
                    <Badge tone="success">Your field</Badge>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant={active ? 'primary' : 'secondary'}
                        isLoading={isSaving && active}
                        onClick={() => (active ? onChoose(field.id) : onPreview(field.id))}
                      >
                        {active ? 'Make this my field' : 'Preview'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
